import {
  allocateLineDiscounts,
  codeExcludesAddOns,
  toDiscountBaseItem,
  type LineDiscountCode,
} from "@obby/lib";

/**
 * Discount codes (and gift cards entered as codes) on a basket checkout, as
 * both basket checkouts handle them: the widget's full-page checkout and the
 * webcomponents overlay Website v2 checks out through. Which codes may be
 * added together, what the validate call is asked, what goes on the wire and
 * how the applied codes split between "pay now" and a deposit's later
 * balance live here once, so the two cannot drift.
 */

/** The fields of a `/discounts/validate` `discount` payload the checkouts
 *  read. The payload carries more; nothing else here depends on it. */
export type ValidatedDiscount = {
  type?: string;
  discountType?: string;
  value?: number;
  amount?: number;
  nature?: string;
  multipleUsageGiftCard?: unknown;
  hasRestrictions?: boolean;
  restrictionItemType?: string;
  isForAllClasses?: boolean;
  coursesIDs?: string[];
  isForAllProducts?: boolean;
  productsIDs?: string[];
  /** DEV-327: the code leaves add-ons at full price (promo codes only —
   *  read through codeExcludesAddOns, which ignores it on gift cards). */
  excludeAddOns?: boolean | null;
  [key: string]: unknown;
};

/** A code the buyer has applied, plus the validated payload
 *  `/discounts/validate` returned for it. */
export type AppliedDiscount = { code: string; data: ValidatedDiscount };

/** The most codes one order can carry (the server's stack limit too). */
export const MAX_DISCOUNT_CODES = 5;

/** A gift card applied as a discount (vs a discount code). Mirrors the
 *  server's `discountType === "giftcard"` split. */
export function isGiftCardDiscount(
  d: { nature?: string; multipleUsageGiftCard?: unknown } | null | undefined,
): boolean {
  return d?.nature === "GIFTCARD" || !!d?.multipleUsageGiftCard;
}

/** Whether a validated discount payload is a percentage off. */
export function isPercentageDiscount(
  d: Pick<ValidatedDiscount, "type" | "discountType"> | null | undefined,
): boolean {
  return (
    d?.type === "percentage" ||
    d?.type === "PERCENTAGE" ||
    d?.discountType === "PERCENTAGE"
  );
}

/**
 * Why `code` can't be added to `applied` before it is even validated, or
 * null. One code unless the school allows stacking, never the same code
 * twice, never more than MAX_DISCOUNT_CODES. (The server enforces the same.)
 */
export function discountCodeAddError({
  code,
  applied,
  allowStacking,
}: {
  code: string;
  applied: AppliedDiscount[];
  allowStacking: boolean;
}): string | null {
  const trimmed = code.trim().toLowerCase();
  if (applied.some((d) => d.code.toLowerCase() === trimmed)) {
    return "That code is already applied.";
  }
  if (!allowStacking && applied.length >= 1) {
    return "Only one discount code can be used here.";
  }
  if (applied.length >= MAX_DISCOUNT_CODES) {
    return `You can apply at most ${MAX_DISCOUNT_CODES} codes.`;
  }
  return null;
}

/**
 * Why a code that validated can't join `applied`, or null. A gift card is a
 * payment, so it is never combined with another code, and a request (which
 * collects nothing until the partner confirms) takes none at all — the
 * server refuses both.
 */
export function validatedDiscountError({
  discount,
  applied,
  isRequestToBook,
}: {
  discount: ValidatedDiscount | null | undefined;
  applied: AppliedDiscount[];
  isRequestToBook: boolean;
}): string | null {
  const isGift = isGiftCardDiscount(discount);
  if (isGift && isRequestToBook) {
    return "Gift cards can't be used on a booking request.";
  }
  const hasGiftCard = applied.some((d) => isGiftCardDiscount(d.data));
  if ((isGift && applied.length >= 1) || (!isGift && hasGiftCard)) {
    return "Gift cards can't be combined with other codes.";
  }
  return null;
}

type DiscountValidateItem = {
  type: string;
  courseId?: string | null;
  productId?: string | null;
  bundleTemplateId?: string | null;
};

/** The `/discounts/validate` request body for `code` against this basket. */
export function discountValidateBody({
  code,
  email,
  schoolId,
  items,
}: {
  code: string;
  email: string;
  schoolId: string | undefined;
  items: DiscountValidateItem[];
}) {
  const idsOf = (type: string, key: "courseId" | "productId" | "bundleTemplateId") => [
    ...new Set(
      items
        .filter((i) => i.type === type && i[key])
        .map((i) => i[key] as string),
    ),
  ];
  return {
    code: code.trim(),
    email: email || "",
    schoolId,
    courseIds: idsOf("booking", "courseId"),
    productIds: idsOf("product", "productId"),
    bundleIds: idsOf("bundle", "bundleTemplateId"),
  };
}

/** The `discountCodes` checkout field: absent (not []) with none applied.
 *  Codes are kept exactly as validated — gift card codes are stored lowercase
 *  and the checkout-time lookup is an exact match. */
export function discountCodesForPayload(
  applied: AppliedDiscount[],
): string[] | undefined {
  return applied.length ? applied.map((d) => d.code) : undefined;
}

/** The "N% off" badge of the single-code field (none when codes stack). */
export function singleCodeDiscountPercentage(
  applied: AppliedDiscount[],
  allowStacking: boolean,
): number | undefined {
  const d = !allowStacking ? applied[0]?.data : undefined;
  if (!d || !isPercentageDiscount(d)) return undefined;
  return d.value || d.amount;
}

/** The basket-item fields the discount split reads. */
export type DiscountShareItem = {
  id: string;
  type: string;
  courseId?: string | null;
  productId?: string | null;
  price: number;
  currentPrice?: number | null;
  quantity: number;
  isDeposit?: boolean;
  depositAmount?: number | null;
  remainingBalance?: number | null;
  isBalancePayment?: boolean;
  priceIsAuthoritative?: boolean;
  /** Booking items: the server-resolved add-on total inside the charged
   *  price, pence per unit (basket validation stamps it). The line model
   *  prices it as its own line (DEV-327). Absent = no add-ons. */
  addOnTotalPence?: number | null;
};

/**
 * Splits the applied codes into the share that comes off what the customer
 * pays NOW and the share that lands on a deposit's later balance.
 *
 * Discounts the full CONTRACT (deposit + balance), then takes only the
 * now-share for a deposit item — exactly what the server charge does — so the
 * displayed "Pay" total equals the PaymentIntent. Non-deposit / balance lines
 * discount on their charged price. Allocation and split come from the shared
 * @obby/lib module the server uses, so client and charge can't drift.
 */
export function computeDiscountShares(
  appliedDiscounts: AppliedDiscount[],
  items: DiscountShareItem[],
  total: number,
): { now: number; later: number } {
  if (!appliedDiscounts.length) return { now: 0, later: 0 };
  // Bundles are class-eligible (applyDiscountStack.isEligible). The client
  // can't do the server's bundle→listing cross-check, so it approximates via
  // coursesIDs; the dominant isForAllClasses case is exact.
  const eligibleFor = (d: ValidatedDiscount, it: DiscountShareItem) => {
    if (!d.hasRestrictions) return true;
    const rt = d.restrictionItemType;
    if (rt === "class" && (it.type === "booking" || it.type === "bundle"))
      return (
        !!d.isForAllClasses ||
        (it.courseId != null && (d.coursesIDs || []).includes(it.courseId))
      );
    if (rt === "product" && it.type === "product")
      return (
        !!d.isForAllProducts ||
        (it.productId != null && (d.productsIDs || []).includes(it.productId))
      );
    return false;
  };
  // Discount the full CONTRACT (deposit + balance) then take only the NOW-share
  // for a deposit item — exactly what the server charge does. An item's add-ons
  // are their own line (DEV-327): discounted unless the code excludes them,
  // always off the charge-now, never a deposit row. The line model is the
  // shared @obby/lib one the server charges with, so client and charge can't
  // drift.
  const chargedUnitPrice = (it: DiscountShareItem) =>
    it.isBalancePayment || it.priceIsAuthoritative ? it.price : it.currentPrice ?? it.price;
  const { nowShare, total: allocatedTotal } = allocateLineDiscounts(
    items.map((it) =>
      toDiscountBaseItem({
        id: it.id,
        price: chargedUnitPrice(it),
        quantity: it.quantity,
        isDeposit: it.isDeposit,
        depositAmount: it.depositAmount ?? undefined,
        remainingBalance: it.remainingBalance ?? undefined,
        addOnTotalPence: it.addOnTotalPence ?? undefined,
      }),
    ),
    appliedDiscounts.map(
      ({ data: d }): LineDiscountCode => ({
        type: isPercentageDiscount(d) ? "percentage" : "fixed",
        value: d.value || d.amount || 0,
        eligibleItemIds: items.filter((it) => eligibleFor(d, it)).map((it) => it.id),
        // `multipleUsageGiftCard` is typed unknown on the wire; the helper
        // only asks whether it is set.
        excludeAddOns: codeExcludesAddOns({
          excludeAddOns: d.excludeAddOns,
          nature: d.nature,
          multipleUsageGiftCard: !!d.multipleUsageGiftCard,
        }),
      }),
    ),
  );
  // The rest of the contract-level discount lands on the balance leg — the
  // "Remaining balance" row must reflect it or the numbers contradict.
  return {
    now: Math.min(nowShare, total),
    later: Math.max(0, allocatedTotal - nowShare),
  };
}
